import {
  Flex,
  ReportSectionItemContent,
  ReportSectionItemTable,
  type ReportSectionItemTableProps,
  type ReportTableCell,
  Text,
} from '@/components';
import type { LedgerKsFundAdditionalIncomeAndExpenditureInfoSectionProps } from '@/features/mold/models/ledger';
import { isBefore2025 } from '@/features/project/review-sheet/utils/project';
import { useLocaledAmount } from '@/providers';
import { formatValueByUnit } from '@/utils';
import { useTranslation } from 'react-i18next';
import {
  LEDGER_BG_COLOR,
  LEDGER_BORDER,
  LEDGER_PADDING,
  LEDGER_RIGHT_ALIGNED_STYLE,
} from '../../../utils';

type Value = string | number | null | undefined;

export type IncomeAndExpenditureItem = {
  title: string;
  beginningOfTheFundBalance: number | null | undefined;
  estimatedGrantedAmountByTheCountry: number | null | undefined;
  estimatedInvestmentIncome: number | null | undefined;
  estimatedNationalEquivalentOfInvestmentIncome: number | null | undefined;
  estimatedBusinessIncome: number | null | undefined;
  estimatedNationalEquivalentOfBusinessIncome: number | null | undefined;
  estimatedOtherIncomeAmount: number | null | undefined;
  totalIncome: Value;
  estimatedBusinessExpense: number | null | undefined;
  estimatedAdministrativeExpense: number | null | undefined;
  estimatedAdministrativeExpenseRate: string;
  totalExpenditure: Value;
  estimatedChoreExpensesOfFundEstablishing: number | null | undefined;
  estimatedPersonnelExpensesOfFundEstablishing: number | null | undefined;
  estimatedAmountReturnedToTheNationalTreasury: number | null | undefined;
  estimatedAmountReturnedToTheOrganization: number | null | undefined;
  fundBalanceAtTheEndOfYear: Value;
  estimatedNationalEquivalentOfTheBalance: number | null | undefined;
  estimatedChoreExpensesNotFromThisFund: number | null | undefined;
  estimatedPersonnelExpensesNotFromThisFund: number | null | undefined;
  estimatedTotalAmountClericalExpenses: Value;
};

const SUB_TITLE_CELL_WIDTH = 90;
const BREAKDOWN_TITLE_CELL_WIDTH = 110;
const TITLE_CELL_WIDTH = SUB_TITLE_CELL_WIDTH + BREAKDOWN_TITLE_CELL_WIDTH;

export const KsFundIncomeAndExpenditureItem = ({
  items,
  fiscalYear,
  calcAdministrativeExpensesRate,
}: {
  items: IncomeAndExpenditureItem[];
  fiscalYear?: number;
  calcAdministrativeExpensesRate: LedgerKsFundAdditionalIncomeAndExpenditureInfoSectionProps['utils']['calcAdministrativeExpensesRate'];
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const formatValue = (value: number | null | undefined) => {
    return formatValueByUnit(value, {
      unit,
      operation: 'round',
      showUnitLabel: false,
    });
  };

  return (
    <div>
      <ReportSectionItemContent
        isTable
        value={
          <Flex style={{ width: '100%', boxSizing: 'border-box' }}>
            <ReportSectionItemTable
              header={[
                {
                  type: 'text',
                  value: ' ',
                  ratio: 10,
                  wrapperStyle: {
                    borderRight: LEDGER_BORDER,
                    borderBottom: LEDGER_BORDER,
                    width: TITLE_CELL_WIDTH - 1,
                  },
                },
              ]}
              data={[
                {
                  row: [
                    {
                      type: 'text',
                      value: t(
                        'fund-balance-at-the-end-of-previous-fiscal-year',
                      ),
                      wrapperStyle: {
                        borderRight: LEDGER_BORDER,
                        borderBottom: LEDGER_BORDER,
                        width: TITLE_CELL_WIDTH - 1,
                      },
                    },
                  ],
                },
              ]}
              tableWidth={`${TITLE_CELL_WIDTH}px`}
            />
            <ReportSectionItemTable
              header={[
                ...(items.map((item) => ({
                  type: 'text',
                  value: item.title,
                  ratio: 10,
                  align: 'center',
                  wrapperStyle: {
                    borderBottom: LEDGER_BORDER,
                  },
                })) satisfies ReportSectionItemTableProps['header']),
              ]}
              data={[
                {
                  row: [
                    ...(items.map((item) => ({
                      type: 'text',
                      value: formatValue(item.beginningOfTheFundBalance),
                      align: 'right',
                      wrapperStyle: { borderBottom: LEDGER_BORDER },
                    })) as ReportTableCell[]),
                  ],
                },
              ]}
              tableWidth="792px"
            />
          </Flex>
        }
      />
      <Flex
        style={{
          width: '100%',
          breakInside: 'avoid',
        }}
      >
        <Flex
          align="center"
          style={{
            padding: LEDGER_PADDING,
            background: LEDGER_BG_COLOR,
            width: SUB_TITLE_CELL_WIDTH,
            minWidth: SUB_TITLE_CELL_WIDTH,
            borderBottom: LEDGER_BORDER,
            boxSizing: 'border-box',
          }}
        >
          <Text variant="footnote" text={t('income')} />
        </Flex>
        <Flex vertical style={{ width: '100%' }}>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={t('granted-amount-by-the-country')}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={formatValue(item.estimatedGrantedAmountByTheCountry)}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('investment-income')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={formatValue(item.estimatedInvestmentIncome)}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('national-equivalent-amount-of-that')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={formatValue(
                  item.estimatedNationalEquivalentOfInvestmentIncome,
                )}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('business-income')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={formatValue(item.estimatedBusinessIncome)}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('national-equivalent-amount-of-that')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={formatValue(
                  item.estimatedNationalEquivalentOfBusinessIncome,
                )}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('others')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={formatValue(item.estimatedOtherIncomeAmount)}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('total-amount')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => {
              const value =
                typeof item.totalIncome === 'number' ||
                typeof item.totalIncome === 'bigint'
                  ? formatValue(Number(item.totalIncome))
                  : item.totalIncome;
              return (
                <ReportSectionItemContent
                  key={item.title}
                  value={value}
                  wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                  valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
                />
              );
            })}
          </Flex>
        </Flex>
      </Flex>

      <Flex
        style={{
          width: '100%',
          breakInside: 'avoid',
        }}
      >
        <Flex
          align="center"
          style={{
            padding: LEDGER_PADDING,
            background: LEDGER_BG_COLOR,
            width: SUB_TITLE_CELL_WIDTH,
            minWidth: SUB_TITLE_CELL_WIDTH,
            borderBottom: LEDGER_BORDER,
            boxSizing: 'border-box',
          }}
        >
          <Text variant="footnote" text={t('expense')} />
        </Flex>
        <Flex vertical style={{ width: '100%' }}>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('business-expenses')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={formatValue(item.estimatedBusinessExpense)}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('administrative-expenses')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={formatValue(item.estimatedAdministrativeExpense)}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('administrative-expenses-rate')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={calcAdministrativeExpensesRate(
                  item.estimatedAdministrativeExpense ?? 0,
                  item.totalExpenditure ?? 0,
                )}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('chore-expenses-of-fund-establishing-of-that')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={formatValue(
                  item.estimatedChoreExpensesOfFundEstablishing,
                )}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('personnel-expenses-of-fund-establishing-of-that')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                value={formatValue(
                  item.estimatedPersonnelExpensesOfFundEstablishing,
                )}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('total-amount')}
                wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              />
            </Flex>
            {items.map((item) => {
              const totalExpenditure = item.totalExpenditure;
              const value =
                typeof totalExpenditure === 'number' ||
                typeof totalExpenditure === 'bigint'
                  ? formatValue(Number(totalExpenditure))
                  : totalExpenditure;
              return (
                <ReportSectionItemContent
                  key={item.title}
                  value={value}
                  wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                  valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
                />
              );
            })}
          </Flex>
        </Flex>
      </Flex>
      <Flex>
        <Flex style={{ width: TITLE_CELL_WIDTH - 1 }}>
          <ReportSectionItemContent
            value={t('returned-to-the-national-treasury')}
          />
        </Flex>
        {items.map((item) => (
          <ReportSectionItemContent
            key={item.title}
            wrapperStyle={{ borderLeft: LEDGER_BORDER }}
            value={formatValue(
              item.estimatedAmountReturnedToTheNationalTreasury,
            )}
            valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
          />
        ))}
      </Flex>
      <Flex>
        <Flex style={{ width: TITLE_CELL_WIDTH - 1 }}>
          <ReportSectionItemContent
            value={
              isBefore2025(fiscalYear ? { fiscalYear } : undefined)
                ? t('returned-to-the-organization-before-2025')
                : t('returned-to-the-organization')
            }
          />
        </Flex>
        {items.map((item) => (
          <ReportSectionItemContent
            key={item.title}
            wrapperStyle={{ borderLeft: LEDGER_BORDER }}
            value={formatValue(item.estimatedAmountReturnedToTheOrganization)}
            valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
          />
        ))}
      </Flex>
      <Flex>
        <Flex style={{ width: TITLE_CELL_WIDTH - 1 }}>
          <ReportSectionItemContent
            value={t('fund-balance-at-the-end-of-fiscal-year')}
          />
        </Flex>
        {items.map((item) => {
          const fundBalanceAtTheEndOfYear = item.fundBalanceAtTheEndOfYear;
          const value =
            typeof fundBalanceAtTheEndOfYear === 'number' ||
            typeof fundBalanceAtTheEndOfYear === 'bigint'
              ? formatValue(Number(fundBalanceAtTheEndOfYear))
              : fundBalanceAtTheEndOfYear;
          return (
            <ReportSectionItemContent
              key={item.title}
              value={value}
              wrapperStyle={{ borderLeft: LEDGER_BORDER }}
              valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
            />
          );
        })}
      </Flex>
      <Flex>
        <Flex style={{ width: TITLE_CELL_WIDTH - 1 }}>
          <ReportSectionItemContent
            value={t('national-equivalent-amount-of-that')}
          />
        </Flex>
        {items.map((item) => (
          <ReportSectionItemContent
            key={item.title}
            wrapperStyle={{ borderLeft: LEDGER_BORDER }}
            value={formatValue(item.estimatedNationalEquivalentOfTheBalance)}
            valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
          />
        ))}
      </Flex>
      <Flex
        style={{
          width: '100%',
          breakInside: 'avoid',
          borderBottom: 0,
        }}
      >
        <Flex
          align="center"
          style={{
            padding: LEDGER_PADDING,
            width: SUB_TITLE_CELL_WIDTH,
            minWidth: SUB_TITLE_CELL_WIDTH,
            background: LEDGER_BG_COLOR,
            boxSizing: 'border-box',
          }}
        >
          <Text variant="footnote" text={t('clerical-personnel-expenses')} />
        </Flex>
        <Flex vertical style={{ width: '100%' }}>
          <Flex style={{ height: '100%' }}>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('chore-expense')}
                wrapperStyle={{
                  width: BREAKDOWN_TITLE_CELL_WIDTH - 1,
                  borderLeft: LEDGER_BORDER,
                }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                value={formatValue(item.estimatedChoreExpensesNotFromThisFund)}
                wrapperStyle={{
                  borderLeft: LEDGER_BORDER,
                  flex: 1,
                }}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex style={{ height: '100%' }}>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                value={t('personnel-expense')}
                wrapperStyle={{
                  width: BREAKDOWN_TITLE_CELL_WIDTH - 1,
                  borderLeft: LEDGER_BORDER,
                }}
              />
            </Flex>
            {items.map((item) => (
              <ReportSectionItemContent
                key={item.title}
                value={formatValue(
                  item.estimatedPersonnelExpensesNotFromThisFund,
                )}
                wrapperStyle={{
                  borderLeft: LEDGER_BORDER,
                  flex: 1,
                }}
                valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
              />
            ))}
          </Flex>
          <Flex style={{ height: '100%' }}>
            <Flex style={{ width: BREAKDOWN_TITLE_CELL_WIDTH - 1 }}>
              <ReportSectionItemContent
                isLast
                value={t('total-amount')}
                wrapperStyle={{
                  width: BREAKDOWN_TITLE_CELL_WIDTH - 1,
                  borderLeft: LEDGER_BORDER,
                }}
              />
            </Flex>

            {items.map((item) => {
              const estimatedTotalAmountClericalExpenses =
                item.estimatedTotalAmountClericalExpenses;
              const value =
                typeof estimatedTotalAmountClericalExpenses === 'number' ||
                typeof estimatedTotalAmountClericalExpenses === 'bigint'
                  ? formatValue(Number(estimatedTotalAmountClericalExpenses))
                  : estimatedTotalAmountClericalExpenses;

              return (
                <ReportSectionItemContent
                  isLast
                  key={item.title}
                  value={value}
                  wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                  valueWrapperStyle={LEDGER_RIGHT_ALIGNED_STYLE}
                />
              );
            })}
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

export const emptyIncomeAndExpenditureItem: IncomeAndExpenditureItem[] = [
  {
    title: '',
    beginningOfTheFundBalance: null,
    estimatedGrantedAmountByTheCountry: null,
    estimatedInvestmentIncome: null,
    estimatedNationalEquivalentOfInvestmentIncome: null,
    estimatedBusinessIncome: null,
    estimatedNationalEquivalentOfBusinessIncome: null,
    estimatedOtherIncomeAmount: null,
    totalIncome: null,
    estimatedBusinessExpense: null,
    estimatedAdministrativeExpense: null,
    estimatedAdministrativeExpenseRate: '',
    estimatedChoreExpensesOfFundEstablishing: null,
    estimatedPersonnelExpensesOfFundEstablishing: null,
    totalExpenditure: null,
    estimatedAmountReturnedToTheNationalTreasury: null,
    estimatedAmountReturnedToTheOrganization: null,
    fundBalanceAtTheEndOfYear: null,
    estimatedNationalEquivalentOfTheBalance: null,
    estimatedChoreExpensesNotFromThisFund: null,
    estimatedPersonnelExpensesNotFromThisFund: null,
    estimatedTotalAmountClericalExpenses: null,
  },
];
